/**
 * Hook de polling adaptatif pour garder une liste synchronisée en temps réel
 * sans forcer l'utilisateur à appuyer sur F5.
 *
 * - Quand l'onglet est au premier plan : poll toutes les `activeMs` (15s par défaut)
 * - Quand l'onglet est caché / en arrière-plan : poll toutes les `inactiveMs` (60s)
 * - Quand l'onglet repasse en premier plan : déclenche immédiatement un refresh
 *
 * Usage :
 *   useAutoRefresh(fetchList);
 *   useAutoRefresh(fetchList, { activeMs: 10000, inactiveMs: 120000, enabled: true });
 */
import { useEffect, useRef } from 'react';

export function useAutoRefresh(fetchFn, { activeMs = 15000, inactiveMs = 60000, enabled = true } = {}) {
    const fnRef = useRef(fetchFn);
    fnRef.current = fetchFn;

    useEffect(() => {
        if (!enabled) return undefined;
        let timerId = null;
        let stopped = false;

        const schedule = () => {
            if (stopped) return;
            const delay = document.visibilityState === 'visible' ? activeMs : inactiveMs;
            timerId = setTimeout(async () => {
                if (stopped) return;
                try {
                    await fnRef.current?.();
                } catch { /* on ignore les erreurs réseau transitoires */ }
                schedule();
            }, delay);
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                // L'onglet revient → refresh immédiat puis reprise du cycle
                if (timerId) clearTimeout(timerId);
                (async () => {
                    try { await fnRef.current?.(); } catch {}
                    schedule();
                })();
            } else {
                // Reset du timer pour cadencer à inactiveMs
                if (timerId) clearTimeout(timerId);
                schedule();
            }
        };

        document.addEventListener('visibilitychange', onVisibility);
        schedule();
        return () => {
            stopped = true;
            if (timerId) clearTimeout(timerId);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled, activeMs, inactiveMs]);
}
